import { useState, useEffect, useCallback } from 'react';
import { useSelectedBusinessId } from '../hooks/useSelectedBusiness';
import { Card, CardHeader, Button, Input, PageHeader, Badge } from '../components/ui';
import { clsx } from 'clsx';
import {
  Calendar,
  TrendingUp,
  Eye,
  Route,
  CheckCircle,
  Plus,
  Save,
  Trash2,
  Loader2,
  AlertCircle,
  BarChart3,
  ListOrdered,
  MapPin,
  Link2,
} from 'lucide-react';
import { businessApi, reportsApi, yandexApi, keywordsApi, type Report, type CreateReportInput, type Keyword, type KeywordPosition, type MetrikaPrefillData, type ReportType } from '../lib/api';

type ReportPeriodMode = 'month' | 'range';
type KeywordRow = { keyword: string; positionIn3km: string; positionInRegion: string };

function createEmptyKeywordRow(): KeywordRow {
  return { keyword: '', positionIn3km: '', positionInRegion: '' };
}

function getMonthDateRange(month: string): { dateFrom: string; dateTo: string } {
  const [rawYear, rawMonth] = month.split('-');
  const year = Number(rawYear);
  const monthIndex = Number(rawMonth) - 1;

  if (!year || Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    const today = new Date();
    return {
      dateFrom: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10),
      dateTo: new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10),
    };
  }

  return {
    dateFrom: new Date(year, monthIndex, 1).toISOString().slice(0, 10),
    dateTo: new Date(year, monthIndex + 1, 0).toISOString().slice(0, 10),
  };
}

function parseReportPeriod(period: string): { mode: ReportPeriodMode; month: string; dateFrom: string; dateTo: string } {
  const rangeMatch = period.match(/^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/);
  if (rangeMatch) {
    return {
      mode: 'range',
      month: rangeMatch[1].slice(0, 7),
      dateFrom: rangeMatch[1],
      dateTo: rangeMatch[2],
    };
  }

  const month = /^\d{4}-\d{2}$/.test(period) ? period : new Date().toISOString().slice(0, 7);
  const { dateFrom, dateTo } = getMonthDateRange(month);
  return { mode: 'month', month, dateFrom, dateTo };
}

function formatPeriodLabel(period: string): string {
  const rangeMatch = period.match(/^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/);
  if (rangeMatch) {
    const from = new Date(`${rangeMatch[1]}T00:00:00`);
    const to = new Date(`${rangeMatch[2]}T00:00:00`);
    return `${from.toLocaleDateString('ru-RU')} - ${to.toLocaleDateString('ru-RU')}`;
  }

  const [year, month] = period.split('-');
  const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  return `${months[parseInt(month) - 1]} ${year}`;
}

function parseKeywordPositionsValue(value: unknown): KeywordPosition[] {
  if (Array.isArray(value)) {
    return value as KeywordPosition[];
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as KeywordPosition[]) : [];
    } catch {
      return [];
    }
  }

  return [];
}

function mapKeywordPositionsToRows(value: unknown): KeywordRow[] {
  const keywordPositions = parseKeywordPositionsValue(value);
  if (keywordPositions.length === 0) {
    return [createEmptyKeywordRow()];
  }

  return keywordPositions.map((item) => ({
    keyword: item.keyword || '',
    positionIn3km: String(item.currentPosition ?? ''),
    positionInRegion: String(item.previousPosition ?? ''),
  }));
}

function mapSemanticKeywordsToRows(items: Keyword[]): KeywordRow[] {
  if (items.length === 0) {
    return [createEmptyKeywordRow()];
  }

  return items.map((item) => ({
    keyword: item.keyword || '',
    positionIn3km: item.currentPosition ? String(item.currentPosition) : '',
    positionInRegion: item.previousPosition ? String(item.previousPosition) : '',
  }));
}

export function Reports() {
  // Active client comes from the URL (`/client/:id/*`) — single source of truth.
  // Null when not inside a client workspace; '' keeps existing falsy guards working.
  const businessId = useSelectedBusinessId();
  const selectedBusiness = businessId ?? '';

  // Data state
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingShareLink, setIsGeneratingShareLink] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateReportInput>({
    businessId: '',
    period: new Date().toISOString().slice(0, 7), // YYYY-MM
    type: 'MAPS' as ReportType,
    views: 0,
    phoneCalls: 0,
    routes: 0,
    websiteClicks: 0,
    searchImpressions: 0,
    categoryImpressions: 0,
    newReviews: 0,
    averageRating: 0,
    totalReviews: 0,
    respondedReviews: 0,
    stars5: 0,
    stars4: 0,
    stars3: 0,
    stars2: 0,
    stars1: 0,
    trafficSearch: 0,
    trafficMaps: 0,
    trafficNavigator: 0,
    trafficDirect: 0,
    keywordPositions: [],
  });

  // Keywords input rows
  const [keywordRows, setKeywordRows] = useState<KeywordRow[]>([createEmptyKeywordRow()]);
  const [keywordsBulkInput, setKeywordsBulkInput] = useState('');
  const [radiusValuesInput, setRadiusValuesInput] = useState('');
  const [regionValuesInput, setRegionValuesInput] = useState('');
  const [radiusKm, setRadiusKm] = useState<number>(3);
  const [isSyncingSemantics, setIsSyncingSemantics] = useState(false);

  // Yandex Metrika state
  const [metrikaConnected, setMetrikaConnected] = useState(false);
  const [metrikaCounterId, setMetrikaCounterId] = useState('');
  const [adsMetrikaCounterId, setAdsMetrikaCounterId] = useState('');
  const [metrikaCounters, setMetrikaCounters] = useState<Array<{ id: string; name: string }>>([]);
  const [metrikaCountersError, setMetrikaCountersError] = useState<string | null>(null);
  const [isPrefilling, setIsPrefilling] = useState(false);
  const [prefillFields, setPrefillFields] = useState<Set<string>>(new Set());

  // Period state
  const [periodMode, setPeriodMode] = useState<ReportPeriodMode>('month');
  const [dateFrom, setDateFrom] = useState(() => getMonthDateRange(new Date().toISOString().slice(0, 7)).dateFrom);
  const [dateTo, setDateTo] = useState(() => getMonthDateRange(new Date().toISOString().slice(0, 7)).dateTo);

  const getAdsCounterStorageKey = useCallback((businessId: string) => `reports:adsCounter:${businessId}`, []);
  const getMetrikaCounterStorageKey = useCallback((businessId: string) => `reports:metrikaCounter:${businessId}`, []);

  const loadMetrikaCounters = useCallback(async (businessId: string) => {
    try {
      setMetrikaCountersError(null);
      const response = await yandexApi.getMetrikaCounters(businessId);
      setMetrikaCounters(response.data.data.map((counter) => ({
        id: counter.id,
        name: counter.name,
      })));
    } catch {
      setMetrikaCounters([]);
      setMetrikaCountersError('Не удалось загрузить список счётчиков Метрики');
    }
  }, []);

  // On mount: clear the page-load spinner and strip Yandex-Metrika OAuth callback
  // params from the URL. The active client now comes from the route, so there is
  // no business list to fetch here for selection.
  useEffect(() => {
    setIsLoading(false);
    // Strip the Yandex-Metrika OAuth callback params (metrika/reason) from the
    // URL while preserving any other query params that may be present.
    const url = new URL(window.location.href);
    if (url.searchParams.get('metrika')) {
      url.searchParams.delete('metrika');
      url.searchParams.delete('reason');
      const search = url.searchParams.toString();
      window.history.replaceState({}, '', `${url.pathname}${search ? `?${search}` : ''}`);
    }
  }, []);

  // Load reports when business changes
  useEffect(() => {
    if (selectedBusiness) {
      setMetrikaCounterId('');
      setAdsMetrikaCounterId('');
      setPrefillFields(new Set());
      setMetrikaCounters([]);
      setMetrikaCountersError(null);
      loadReports(selectedBusiness);
      setFormData(prev => ({ ...prev, businessId: selectedBusiness }));

      const savedAdsCounter = window.localStorage.getItem(getAdsCounterStorageKey(selectedBusiness))?.trim() ?? '';
      if (savedAdsCounter) {
        setAdsMetrikaCounterId(savedAdsCounter);
      }

      const savedMainCounter = window.localStorage.getItem(getMetrikaCounterStorageKey(selectedBusiness))?.trim() ?? '';
      if (savedMainCounter) {
        setMetrikaCounterId(savedMainCounter);
      }

      // Check Metrika connection status
      yandexApi.getStatus(selectedBusiness)
        .then((res) => {
          const connected = res.data.data.connected;
          setMetrikaConnected(connected);
          if (connected) {
            void loadMetrikaCounters(selectedBusiness);
          }
        })
        .catch(() => {
          setMetrikaConnected(false);
          setMetrikaCounters([]);
        });

      // Load persisted counter ID from business settings
      businessApi.getById(selectedBusiness)
        .then((res) => {
          const dbCounterId = res.data.data.metrikaCounterId?.trim() ?? '';
          const resolvedCounterId = dbCounterId || savedMainCounter;
          setMetrikaCounterId(resolvedCounterId);
          if (resolvedCounterId) {
            window.localStorage.setItem(getMetrikaCounterStorageKey(selectedBusiness), resolvedCounterId);
          }
        })
        .catch(() => {
          // noop: user can still enter the counter manually
        });
    }
  }, [selectedBusiness, getAdsCounterStorageKey, getMetrikaCounterStorageKey, loadMetrikaCounters]);

  const loadReports = async (businessId: string) => {
    try {
      const response = await reportsApi.getReportsByBusiness(businessId);
      setReports(response.data.data);
    } catch (err) {
      console.error('Failed to load reports:', err);
    }
  };

  const loadKeywordRowsFromSemantics = useCallback(async (businessId: string, options?: { silent?: boolean }) => {
    if (!businessId) {
      return;
    }

    try {
      setIsSyncingSemantics(true);
      const response = await keywordsApi.getByBusiness(businessId);
      setKeywordRows(mapSemanticKeywordsToRows(response.data.data));
    } catch (err) {
      if (!options?.silent) {
        setError('Не удалось подтянуть ключи из раздела «Семантика»');
      }
      console.error('Failed to load semantic keywords for report:', err);
    } finally {
      setIsSyncingSemantics(false);
    }
  }, []);

  const handleMetrikaConnect = () => {
    if (!selectedBusiness) return;
    window.location.href = yandexApi.getOAuthStartUrl(selectedBusiness);
  };

  const handleMetrikaDisconnect = async () => {
    try {
      await yandexApi.disconnect(selectedBusiness);
      setMetrikaConnected(false);
      setMetrikaCounters([]);
      setMetrikaCountersError(null);
      setMetrikaCounterId('');
      window.localStorage.removeItem(getMetrikaCounterStorageKey(selectedBusiness));
    } catch {
      setError('Не удалось отключить Яндекс.Метрику');
    }
  };

  const applyPrefillMetrics = useCallback((metrics: MetrikaPrefillData, options?: { adsMode?: boolean }) => {
    const filledFields = new Set<string>(Object.keys(metrics));

    setFormData((prev) => {
      const next = { ...prev, ...metrics };

      if (options?.adsMode) {
        const mappedAdClicks = metrics.websiteClicks ?? 0;
        const mappedAdImpressions = metrics.views ?? 0;
        const mappedCtr = mappedAdImpressions > 0 ? Number(((mappedAdClicks / mappedAdImpressions) * 100).toFixed(2)) : 0;

        if ((prev.adClicks ?? 0) === 0 && mappedAdClicks > 0) {
          next.adClicks = mappedAdClicks;
          filledFields.add('adClicks');
        }
        if ((prev.adImpressions ?? 0) === 0 && mappedAdImpressions > 0) {
          next.adImpressions = mappedAdImpressions;
          filledFields.add('adImpressions');
        }
        if ((prev.adCtr ?? 0) === 0 && mappedCtr > 0) {
          next.adCtr = mappedCtr;
          filledFields.add('adCtr');
        }
      }

      return next;
    });

    setPrefillFields(filledFields);
  }, []);

  const handleMetrikaPrefill = useCallback(async (options?: { silent?: boolean }) => {
    const normalizedMainCounterId = metrikaCounterId.trim();
    const normalizedAdsCounterId = adsMetrikaCounterId.trim();
    const useAdsCounter = formData.type === 'ADS' && Boolean(normalizedAdsCounterId);
    const effectiveCounterId = useAdsCounter ? normalizedAdsCounterId : normalizedMainCounterId;
    const shouldSaveCounter = !useAdsCounter;

    if (!effectiveCounterId) {
      if (!options?.silent) {
        setError('Введите ID счётчика Яндекс.Метрики');
      }
      return false;
    }

    if (!selectedBusiness || !formData.period) {
      if (!options?.silent) {
        setError('Выберите бизнес и период отчёта');
      }
      return false;
    }

    setIsPrefilling(true);
    try {
      const prefillParams = periodMode === 'range'
        ? {
            dateFrom,
            dateTo,
            counterId: effectiveCounterId,
            saveCounter: shouldSaveCounter,
          }
        : {
            period: formData.period,
            counterId: effectiveCounterId,
            saveCounter: shouldSaveCounter,
          };

      if (periodMode === 'range') {
        if (!dateFrom || !dateTo) {
          if (!options?.silent) {
            setError('Укажите диапазон дат: от и до');
          }
          return false;
        }
        if (dateFrom > dateTo) {
          if (!options?.silent) {
            setError('Дата начала не может быть позже даты окончания');
          }
          return false;
        }
      }

      const res = await yandexApi.getBusinessAnalytics(selectedBusiness, prefillParams);

      const analytics = res.data.data;
      if (shouldSaveCounter) {
        const savedCounterId = analytics.savedCounterId || analytics.counterId || effectiveCounterId;
        setMetrikaCounterId(savedCounterId);
        window.localStorage.setItem(getMetrikaCounterStorageKey(selectedBusiness), savedCounterId);
      } else {
        setAdsMetrikaCounterId(effectiveCounterId);
        window.localStorage.setItem(getAdsCounterStorageKey(selectedBusiness), effectiveCounterId);
      }
      applyPrefillMetrics(analytics.metrics, { adsMode: formData.type === 'ADS' });
      return true;
    } catch {
      if (!options?.silent) {
        setError('Не удалось получить данные из Метрики. Проверьте ID счётчика.');
      }
      return false;
    } finally {
      setIsPrefilling(false);
    }
  }, [adsMetrikaCounterId, applyPrefillMetrics, dateFrom, dateTo, formData.period, formData.type, getAdsCounterStorageKey, getMetrikaCounterStorageKey, metrikaCounterId, periodMode, selectedBusiness]);

  useEffect(() => {
    if (formData.type === 'KEYWORDS') {
      return;
    }

    const effectiveCounterId = formData.type === 'ADS' ? (adsMetrikaCounterId.trim() || metrikaCounterId.trim()) : metrikaCounterId.trim();
    if (!showForm || selectedReport || !metrikaConnected || !formData.period || !selectedBusiness || !effectiveCounterId) {
      return;
    }

    void handleMetrikaPrefill({ silent: true });
  }, [adsMetrikaCounterId, dateFrom, dateTo, formData.period, formData.type, handleMetrikaPrefill, metrikaConnected, metrikaCounterId, selectedBusiness, selectedReport, showForm]);

  useEffect(() => {
    if (!showForm || formData.type !== 'KEYWORDS' || !selectedBusiness || selectedReport) {
      return;
    }

    void loadKeywordRowsFromSemantics(selectedBusiness, { silent: true });
  }, [formData.type, loadKeywordRowsFromSemantics, selectedBusiness, selectedReport, showForm]);

  const handleInputChange = (field: keyof CreateReportInput, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: typeof value === 'string' && field !== 'period' && field !== 'businessId'
        ? parseFloat(value) || 0
        : value,
    }));
  };

  const parseKeywords = (rows: KeywordRow[]): KeywordPosition[] => {
    return rows
      .map((row) => ({
        keyword: row.keyword.trim(),
        currentPosition: parseInt(row.positionIn3km, 10) || 0,
        previousPosition: parseInt(row.positionInRegion, 10) || 0,
        change: 0,
        views: 0,
      }))
      .filter((row) => row.keyword.length > 0);
  };

  const updateKeywordRow = (index: number, field: keyof KeywordRow, value: string) => {
    setKeywordRows((prev) => prev.map((row, rowIndex) => (
      rowIndex === index
        ? { ...row, [field]: value }
        : row
    )));
  };

  const addKeywordRow = () => {
    setKeywordRows((prev) => [...prev, createEmptyKeywordRow()]);
  };

  const removeKeywordRow = (index: number) => {
    setKeywordRows((prev) => {
      const filtered = prev.filter((_, rowIndex) => rowIndex !== index);
      return filtered.length > 0 ? filtered : [createEmptyKeywordRow()];
    });
  };

  const appendKeywordsFromList = () => {
    const keywords = keywordsBulkInput
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (keywords.length === 0) {
      return;
    }

    setKeywordRows((prev) => {
      const prepared = keywords.map((keyword) => ({
        keyword,
        positionIn3km: '',
        positionInRegion: '',
      }));

      if (prev.length === 1 && !prev[0].keyword.trim() && !prev[0].positionIn3km && !prev[0].positionInRegion) {
        return prepared;
      }

      return [...prev, ...prepared];
    });

    setKeywordsBulkInput('');
  };

  const parsePositionValues = (value: string): string[] => {
    return value
      .split(/[\n,;\t]+/g)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const fillKeywordPositionsFromValues = (target: 'radius' | 'region') => {
    const rawValues = target === 'radius' ? radiusValuesInput : regionValuesInput;
    const values = parsePositionValues(rawValues);
    if (values.length === 0) {
      return;
    }

    setKeywordRows((prev) => {
      const keywordRowIndexes = prev
        .map((row, rowIndex) => (row.keyword.trim() ? rowIndex : -1))
        .filter((rowIndex) => rowIndex >= 0);

      if (keywordRowIndexes.length === 0) {
        return prev;
      }

      const nextRows = [...prev];
      keywordRowIndexes.forEach((rowIndex, index) => {
        const value = values[index];
        if (value === undefined) {
          return;
        }

        const row = nextRows[rowIndex];
        nextRows[rowIndex] = target === 'radius'
          ? { ...row, positionIn3km: value }
          : { ...row, positionInRegion: value };
      });

      return nextRows;
    });
  };

  const handleSave = async () => {
    if (!formData.businessId || !formData.period) {
      setError('Выберите бизнес и период');
      return;
    }

    let effectivePeriod = formData.period;
    if (periodMode === 'range') {
      if (!dateFrom || !dateTo) {
        setError('Укажите диапазон дат: от и до');
        return;
      }
      if (dateFrom > dateTo) {
        setError('Дата начала не может быть позже даты окончания');
        return;
      }
      effectivePeriod = `${dateFrom}..${dateTo}`;
    }

    try {
      setIsSaving(true);
      setError(null);

      let keywordPositionsForSave: KeywordPosition[] | undefined;
      if (formData.type === 'KEYWORDS') {
        keywordPositionsForSave = parseKeywords(keywordRows);
        if (keywordPositionsForSave.length === 0 && selectedBusiness) {
          try {
            const semanticResponse = await keywordsApi.getByBusiness(selectedBusiness);
            const semanticRows = mapSemanticKeywordsToRows(semanticResponse.data.data);
            setKeywordRows(semanticRows);
            keywordPositionsForSave = parseKeywords(semanticRows);
          } catch (semanticError) {
            console.error('Failed to auto-load semantic keywords before save:', semanticError);
          }
        }
      }

      const dataToSave = {
        ...formData,
        period: effectivePeriod,
        keywordPositions: formData.type === 'KEYWORDS' ? (keywordPositionsForSave ?? []) : undefined,
      };

      const response = await reportsApi.upsertReport(dataToSave);

      // Reload reports
      await loadReports(selectedBusiness);

      // Select the new/updated report
      setSelectedReport(response.data.data);
      setShowForm(false);
    } catch (err) {
      setError('Не удалось сохранить отчёт');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (reportId: string) => {
    if (!confirm('Удалить этот отчёт?')) return;

    try {
      await reportsApi.deleteReport(reportId);
      await loadReports(selectedBusiness);
      setSelectedReport(null);
    } catch (err) {
      setError('Не удалось удалить отчёт');
      console.error(err);
    }
  };

  const handleCopyClientShareLink = async () => {
    if (!selectedBusiness) {
      setError('Сначала выберите бизнес');
      return;
    }

    try {
      setIsGeneratingShareLink(true);
      setError(null);
      const response = await reportsApi.createShareLink(selectedBusiness);
      const link = response.data.data.url;

      let copied = false;
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(link);
          copied = true;
        } catch (clipboardError) {
          console.warn('Clipboard write failed, falling back to manual copy prompt', clipboardError);
        }
      }

      if (copied) {
        window.alert('Ссылка для клиента скопирована');
      } else {
        window.prompt('Скопируйте ссылку для клиента:', link);
      }
    } catch (err: unknown) {
      const apiError = (
        typeof err === 'object'
        && err !== null
        && 'response' in err
      )
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      const genericMessage = err instanceof Error ? err.message : null;
      setError(apiError || genericMessage || 'Не удалось создать ссылку для клиента');
    } finally {
      setIsGeneratingShareLink(false);
    }
  };

  const handleNewReport = () => {
    const defaultMonth = new Date().toISOString().slice(0, 7);
    const defaultRange = getMonthDateRange(defaultMonth);
    setFormData({
      businessId: selectedBusiness,
      period: defaultMonth,
      type: 'MAPS' as ReportType,
      views: 0,
      phoneCalls: 0,
      routes: 0,
      websiteClicks: 0,
      searchImpressions: 0,
      categoryImpressions: 0,
      newReviews: 0,
      averageRating: 0,
      totalReviews: 0,
      respondedReviews: 0,
      stars5: 0,
      stars4: 0,
      stars3: 0,
      stars2: 0,
      stars1: 0,
      trafficSearch: 0,
      trafficMaps: 0,
      trafficNavigator: 0,
      trafficDirect: 0,
      keywordPositions: [],
    });
    setPeriodMode('month');
    setDateFrom(defaultRange.dateFrom);
    setDateTo(defaultRange.dateTo);
    setKeywordRows([createEmptyKeywordRow()]);
    setRadiusKm(3);
    setKeywordsBulkInput('');
    setRadiusValuesInput('');
    setRegionValuesInput('');
    setSelectedReport(null);
    setPrefillFields(new Set());
    setShowForm(true);
  };

  const handleEditReport = (report: Report) => {
    const parsedPeriod = parseReportPeriod(report.period);
    setFormData({
      businessId: report.businessId,
      period: parsedPeriod.month,
      type: report.type || 'MAPS',
      views: report.views,
      phoneCalls: report.phoneCalls,
      routes: report.routes,
      websiteClicks: report.websiteClicks,
      searchImpressions: report.searchImpressions,
      categoryImpressions: report.categoryImpressions,
      newReviews: report.newReviews,
      averageRating: report.averageRating,
      totalReviews: report.totalReviews,
      respondedReviews: report.respondedReviews,
      stars5: report.stars5,
      stars4: report.stars4,
      stars3: report.stars3,
      stars2: report.stars2,
      stars1: report.stars1,
      trafficSearch: report.trafficSearch,
      trafficMaps: report.trafficMaps,
      trafficNavigator: report.trafficNavigator,
      trafficDirect: report.trafficDirect,
      adImpressions: report.adImpressions ?? 0,
      adClicks: report.adClicks ?? 0,
      adCtr: report.adCtr ?? 0,
      adSpend: report.adSpend ?? 0,
    });
    setPeriodMode(parsedPeriod.mode);
    setDateFrom(parsedPeriod.dateFrom);
    setDateTo(parsedPeriod.dateTo);

    setKeywordRows(mapKeywordPositionsToRows(report.keywordPositions));
    setRadiusKm(3);
    setKeywordsBulkInput('');
    setRadiusValuesInput('');
    setRegionValuesInput('');

    setSelectedReport(report);
    setPrefillFields(new Set());
    setShowForm(true);
  };

  const effectivePrefillCounterId = formData.type === 'ADS'
    ? (adsMetrikaCounterId.trim() || metrikaCounterId.trim())
    : formData.type === 'MAPS'
      ? metrikaCounterId.trim()
      : '';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Отчёты"
        subtitle="Отчёты по картам, рекламе и позициям по ключам"
      />

      <div className="p-6 space-y-6">
        {/* Error message */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
              ✕
            </button>
          </div>
        )}

        {/* Controls */}
        <Card>
          <div className="flex flex-wrap items-center justify-end gap-4">
            {/* Add report button */}
            <div className="flex items-end gap-2">
              <Button
                onClick={handleNewReport}
                icon={<Plus className="w-4 h-4" />}
              >
                Новый отчёт
              </Button>
            </div>
          </div>
        </Card>

        {/* Form for new/edit report */}
        {showForm && (
          <Card>
            <CardHeader
              title={selectedReport ? 'Редактировать отчёт' : 'Новый отчёт'}
              subtitle="Введите данные из Яндекс.Бизнес"
            />

            {formData.type !== 'KEYWORDS' && (
              <div className={clsx('rounded-xl border p-3 mb-4', metrikaConnected ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50')}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${metrikaConnected ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span className="text-sm font-medium text-gray-700">Яндекс.Метрика</span>
                  {!metrikaConnected ? (
                    <button
                      onClick={handleMetrikaConnect}
                      className="text-sm text-primary-600 hover:text-primary-700 underline"
                    >
                      Подключить аккаунт
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleMetrikaConnect}
                        className="text-sm text-primary-600 hover:text-primary-700 underline"
                      >
                        Переподключить аккаунт
                      </button>
                      <input
                        type="text"
                        list="metrika-counter-options"
                        value={metrikaCounterId}
                        onChange={(e) => {
                          const value = e.target.value;
                          setMetrikaCounterId(value);
                          if (selectedBusiness) {
                            window.localStorage.setItem(getMetrikaCounterStorageKey(selectedBusiness), value.trim());
                          }
                        }}
                        placeholder="Основной ID счётчика"
                        className="border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 w-52"
                      />
                      {formData.type === 'ADS' && (
                        <input
                          type="text"
                          list="metrika-counter-options"
                          value={adsMetrikaCounterId}
                          onChange={(e) => {
                            const value = e.target.value;
                            setAdsMetrikaCounterId(value);
                            if (selectedBusiness) {
                              window.localStorage.setItem(getAdsCounterStorageKey(selectedBusiness), value.trim());
                            }
                          }}
                          placeholder="Рекламный счётчик (опц.)"
                          className="border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 w-56"
                        />
                      )}
                      <datalist id="metrika-counter-options">
                        {metrikaCounters.map((counter) => (
                          <option key={counter.id} value={counter.id}>
                            {counter.name}
                          </option>
                        ))}
                      </datalist>
                      <button
                        onClick={() => {
                          void handleMetrikaPrefill();
                        }}
                        disabled={isPrefilling || !effectivePrefillCounterId}
                        className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {isPrefilling ? (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : null}
                        Заполнить из Метрики
                      </button>
                      <button
                        onClick={handleMetrikaDisconnect}
                        className="text-xs text-gray-400 hover:text-red-500 ml-auto"
                      >
                        Отключить
                      </button>
                    </>
                  )}
                </div>
                {metrikaCountersError && (
                  <p className="text-xs text-red-600 mt-2">{metrikaCountersError}</p>
                )}
                {formData.type === 'ADS' && metrikaConnected && (
                  <p className="text-xs text-gray-600 mt-2">
                    Для рекламы можно указать отдельный счётчик Метрики. Если поле пустое, используется основной счётчик.
                  </p>
                )}
                {prefillFields.size > 0 && (
                  <p className="text-xs text-green-600 mt-2">
                    ✓ Поля заполнены из Метрики: {Array.from(prefillFields).join(', ')}. Вы можете отредактировать их вручную.
                  </p>
                )}
              </div>
            )}

            <div className="space-y-6">
              {/* Report type + Period */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Тип отчёта
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: 'MAPS' }))}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors',
                        formData.type === 'MAPS'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      )}
                    >
                      <MapPin className="w-4 h-4" />
                      Карты
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: 'ADS' }))}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors',
                        formData.type === 'ADS'
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      )}
                    >
                      <BarChart3 className="w-4 h-4" />
                      Реклама
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: 'KEYWORDS' }))}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors',
                        formData.type === 'KEYWORDS'
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      )}
                    >
                      <ListOrdered className="w-4 h-4" />
                      Позиции
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Период отчёта
                  </label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setPeriodMode('month')}
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                          periodMode === 'month'
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                        )}
                      >
                        За месяц
                      </button>
                      <button
                        type="button"
                        onClick={() => setPeriodMode('range')}
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                          periodMode === 'range'
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                        )}
                      >
                        По дням
                      </button>
                    </div>

                    {periodMode === 'month' ? (
                      <Input
                        type="month"
                        value={formData.period}
                        onChange={(e) => {
                          const nextMonth = e.target.value;
                          handleInputChange('period', nextMonth);
                          const range = getMonthDateRange(nextMonth);
                          setDateFrom(range.dateFrom);
                          setDateTo(range.dateTo);
                        }}
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          label="От"
                          type="date"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                        />
                        <Input
                          label="До"
                          type="date"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Ads-specific metrics */}
              {formData.type === 'ADS' && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Рекламные метрики
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Input
                      label="Показы рекламы"
                      type="number"
                      value={formData.adImpressions ?? 0}
                      onChange={(e) => handleInputChange('adImpressions', e.target.value)}
                    />
                    <Input
                      label="Клики"
                      type="number"
                      value={formData.adClicks ?? 0}
                      onChange={(e) => handleInputChange('adClicks', e.target.value)}
                    />
                    <Input
                      label="CTR %"
                      type="number"
                      step="0.01"
                      value={formData.adCtr ?? 0}
                      onChange={(e) => handleInputChange('adCtr', e.target.value)}
                    />
                    <Input
                      label="Расход (руб.)"
                      type="number"
                      step="0.01"
                      value={formData.adSpend ?? 0}
                      onChange={(e) => handleInputChange('adSpend', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {formData.type !== 'KEYWORDS' && (
                <>
                  {/* Main metrics */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Основные метрики
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Input
                        label="Просмотры карточки"
                        type="number"
                        value={formData.views}
                        onChange={(e) => handleInputChange('views', e.target.value)}
                      />
                      <Input
                        label="Клики по телефону"
                        type="number"
                        value={formData.phoneCalls}
                        onChange={(e) => handleInputChange('phoneCalls', e.target.value)}
                      />
                      <Input
                        label="Построено маршрутов"
                        type="number"
                        value={formData.routes}
                        onChange={(e) => handleInputChange('routes', e.target.value)}
                      />
                      <Input
                        label="Переходы на сайт"
                        type="number"
                        value={formData.websiteClicks}
                        onChange={(e) => handleInputChange('websiteClicks', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Impressions */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Показы</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Показы в поиске"
                        type="number"
                        value={formData.searchImpressions}
                        onChange={(e) => handleInputChange('searchImpressions', e.target.value)}
                      />
                      <Input
                        label="Показы в категории"
                        type="number"
                        value={formData.categoryImpressions}
                        onChange={(e) => handleInputChange('categoryImpressions', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Traffic sources */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Route className="w-4 h-4" />
                      Источники трафика (%)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Input
                        label="Поиск %"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.trafficSearch}
                        onChange={(e) => handleInputChange('trafficSearch', e.target.value)}
                      />
                      <Input
                        label="Карты %"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.trafficMaps}
                        onChange={(e) => handleInputChange('trafficMaps', e.target.value)}
                      />
                      <Input
                        label="Навигатор %"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.trafficNavigator}
                        onChange={(e) => handleInputChange('trafficNavigator', e.target.value)}
                      />
                      <Input
                        label="Прямой %"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.trafficDirect}
                        onChange={(e) => handleInputChange('trafficDirect', e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}

              {formData.type === 'KEYWORDS' && (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h4 className="font-medium text-gray-900">Позиции по ключам</h4>
                    <button
                      type="button"
                      onClick={() => {
                        void loadKeywordRowsFromSemantics(selectedBusiness);
                      }}
                      disabled={!selectedBusiness || isSyncingSemantics}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                    >
                      {isSyncingSemantics ? (
                        <div className="w-3.5 h-3.5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                      ) : null}
                      Подтянуть из Семантики
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Ключи и позиции для этого отчёта можно подтянуть автоматически из раздела «Семантика».
                  </p>
                  <div className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                    <p className="text-xs text-gray-600 mb-2">
                      Вставьте ключи списком (по одному в строке), затем нажмите добавить.
                    </p>
                    <textarea
                      rows={4}
                      value={keywordsBulkInput}
                      onChange={(e) => setKeywordsBulkInput(e.target.value)}
                      placeholder={'стоматология москва\nлечение кариеса\nбрекеты цена'}
                      className="w-full border border-gray-300 rounded px-2.5 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                    />
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button
                        type="button"
                        onClick={appendKeywordsFromList}
                        className="text-sm text-primary-600 hover:text-primary-700 underline"
                      >
                        + Добавить ключи списком
                      </button>
                    </div>
                  </div>

                  <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <p className="text-xs text-gray-600 mb-2">
                        Вставьте значения для столбца радиуса (по одному в строке).
                      </p>
                      <textarea
                        rows={4}
                        value={radiusValuesInput}
                        onChange={(e) => setRadiusValuesInput(e.target.value)}
                        placeholder={'1\n2\n3\n4'}
                        className="w-full border border-gray-300 rounded px-2.5 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        type="button"
                        onClick={() => fillKeywordPositionsFromValues('radius')}
                        className="mt-2 text-sm text-primary-600 hover:text-primary-700 underline"
                      >
                        Заполнить значения радиуса
                      </button>
                    </div>

                    <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <p className="text-xs text-gray-600 mb-2">
                        Вставьте значения для столбца региона (по одному в строке).
                      </p>
                      <textarea
                        rows={4}
                        value={regionValuesInput}
                        onChange={(e) => setRegionValuesInput(e.target.value)}
                        placeholder={'1\n5\n10\n12'}
                        className="w-full border border-gray-300 rounded px-2.5 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        type="button"
                        onClick={() => fillKeywordPositionsFromValues('region')}
                        className="mt-2 text-sm text-primary-600 hover:text-primary-700 underline"
                      >
                        Заполнить значения региона
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full min-w-[760px]">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left py-2.5 px-3 text-sm font-medium text-gray-600">
                            Ключевой запрос
                          </th>
                          <th className="text-left py-2.5 px-3 text-sm font-medium text-gray-600">
                            <div className="flex items-center gap-2">
                              <span>Позиция в радиусе</span>
                              <select
                                value={radiusKm}
                                onChange={(e) => setRadiusKm(parseInt(e.target.value, 10))}
                                className="border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white"
                              >
                                {Array.from({ length: 10 }, (_, index) => index + 1).map((km) => (
                                  <option key={km} value={km}>
                                    {km} км
                                  </option>
                                ))}
                              </select>
                            </div>
                          </th>
                          <th className="text-left py-2.5 px-3 text-sm font-medium text-gray-600">
                            Позиция в Регионе
                          </th>
                          <th className="w-10 py-2.5 px-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {keywordRows.map((row, index) => (
                          <tr key={index} className="border-b border-gray-100 last:border-0">
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.keyword}
                                onChange={(e) => updateKeywordRow(index, 'keyword', e.target.value)}
                                placeholder="Например: стоматология москва"
                                className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-primary-500"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                value={row.positionIn3km}
                                onChange={(e) => updateKeywordRow(index, 'positionIn3km', e.target.value)}
                                placeholder="0"
                                className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-primary-500"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                value={row.positionInRegion}
                                onChange={(e) => updateKeywordRow(index, 'positionInRegion', e.target.value)}
                                placeholder="0"
                                className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-primary-500"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <button
                                type="button"
                                onClick={() => removeKeywordRow(index)}
                                className="text-red-500 hover:text-red-600 text-xs"
                                title="Удалить строку"
                              >
                                Удалить
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    onClick={addKeywordRow}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-700 underline"
                  >
                    + Добавить ключ
                  </button>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleSave}
                  loading={isSaving}
                  icon={<Save className="w-4 h-4" />}
                >
                  Сохранить
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowForm(false)}
                >
                  Отмена
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Reports list */}
        <Card>
          <CardHeader
            title="Сохранённые отчёты"
            subtitle={`${reports.length} отчётов для выбранного бизнеса`}
            action={(
              <Button
                variant="outline"
                size="sm"
                icon={<Link2 className="w-4 h-4" />}
                onClick={handleCopyClientShareLink}
                loading={isGeneratingShareLink}
              >
                Ссылка для клиента
              </Button>
            )}
          />

          {reports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Нет отчётов для этого бизнеса</p>
              <p className="text-sm">Нажмите "Новый отчёт" чтобы создать первый</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Период
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Тип
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Просмотры
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Звонки
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      AI
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr
                      key={report.id}
                      className="border-b border-gray-50 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {formatPeriodLabel(report.period)}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {report.type === 'ADS' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700">
                            <BarChart3 className="w-3 h-3" />
                            Реклама
                          </span>
                        ) : report.type === 'KEYWORDS' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                            <ListOrdered className="w-3 h-3" />
                            Позиции
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                            <MapPin className="w-3 h-3" />
                            Карты
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-600">
                          {report.type === 'ADS'
                            ? `${(report.adImpressions ?? 0).toLocaleString()} показов`
                            : report.type === 'KEYWORDS'
                              ? `${parseKeywordPositionsValue(report.keywordPositions).length.toLocaleString()} ключей`
                            : report.views.toLocaleString()
                          }
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-600">
                          {report.type === 'ADS'
                            ? `${(report.adClicks ?? 0).toLocaleString()} кликов`
                            : report.type === 'KEYWORDS'
                              ? '—'
                            : report.phoneCalls.toLocaleString()
                          }
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {report.aiSummary ? (
                          <Badge tone="success" className="gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Готово
                          </Badge>
                        ) : (
                          <Badge tone="neutral">
                            Нет
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditReport(report)}
                          >
                            Редактировать
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(report.id)}
                            icon={<Trash2 className="w-4 h-4 text-red-500" />}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* AI Summary preview */}
        {selectedReport?.aiSummary && (
          <Card>
            <CardHeader
              title="AI-резюме"
              subtitle={`Отчёт за ${formatPeriodLabel(selectedReport.period)}`}
            />

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Резюме</h4>
                <p className="text-gray-600">{selectedReport.aiSummary}</p>
              </div>

              {selectedReport.aiAchievements && selectedReport.aiAchievements.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Достижения</h4>
                  <ul className="space-y-1">
                    {selectedReport.aiAchievements.map((achievement, index) => (
                      <li key={index} className="text-gray-600 flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-green-500 mt-0.5" />
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedReport.aiRecommendations && selectedReport.aiRecommendations.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Рекомендации</h4>
                  <ul className="space-y-1">
                    {selectedReport.aiRecommendations.map((rec, index) => (
                      <li key={index} className="text-gray-600 flex items-start gap-2">
                        <span className="text-primary-500 font-medium">{index + 1}.</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedReport.aiComparison && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Сравнение с прошлым периодом</h4>
                  <p className="text-gray-600">{selectedReport.aiComparison}</p>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
